import { useEvents } from "@/lib/hooks/use-events";
import { useToast } from "@/components/ui/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { User } from "@/lib/api/user";
import { getPreferredName } from "@/lib/redacted-display";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";

// This is what the NOTIFICATION_CREATED payload actually looks like
interface NotificationCreatedPayload {
  type: string;
  title: string;
  content: string;
  data?: {
    mentorshipId?: string;
    sdkUrl?: string;
    menteeId?: string;
    mentee?: User;
    [key: string]: unknown;
  };
  recipient: unknown;
  sender?: unknown;
  channels: string[];
}

export function useFirstMentorMessage() {
  const { addEventListener } = useEvents();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleNotificationCreated = (payload: NotificationCreatedPayload) => {
      // Debug: log ALL notifications to understand the structure
      console.log("🔍 NOTIFICATION_CREATED received:", payload);

      // Filter for only NEW_TEEN notifications
      if (payload.type !== "NEW_TEEN" || !payload.data?.mentee) {
        console.log("🚫 Skipping non-NEW_TEEN notification:", payload.type);
        return;
      }

      console.log("🎉 NEW_TEEN notification detected!", payload);

      const mentee = payload.data.mentee;
      const teenName = getPreferredName(mentee);

      console.log("📱 Showing new teen toast for:", teenName);

      // Show polished toast notification
      const { dismiss } = toast({
        title: "New teen assigned",
        description: `You're matched with ${teenName}.`,
        action: (
          <Button
            size="sm"
            onClick={() => {
              navigate({ to: "/new-teens" });
              // close the toast
              dismiss();
            }}
            variant="default"
          >
            Send a message
          </Button>
        ),
        duration: 10000, // Show for 10 seconds
        className:
          "rounded-lg border border-zinc-200/60 border-2 border-blue-500 bg-white/50 backdrop-blur-lg shadow-lg ring-1 ring-black/5",
      });

      // Invalidate queries to refresh notification counts and lists
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.includes("notifications") ||
          query.queryKey.includes("notification-count"),
      });

      // Specifically refetch the new teens page query
      queryClient.invalidateQueries({
        queryKey: ["notifications", { type: "NEW_TEEN", unreadOnly: false }],
      });
    };

    // Add event listeners for notification events
    console.log(
      "Adding event listener for NOTIFICATION_CREATED (filtering for NEW_TEEN)",
    );
    const cleanupCreate = addEventListener<NotificationCreatedPayload>(
      "NOTIFICATION_CREATED",
      handleNotificationCreated,
    );

    return () => {
      console.log("Removing event listener for NOTIFICATION_CREATED");
      cleanupCreate();
    };
  }, [addEventListener, toast, navigate, queryClient]);
}
